import './GridInput.css';

import React, { Dispatch } from 'react';
import {
  CheckboxSelectControl,
  Control,
  ControlItemValue,
  DateTimeInputControl,
  FilterError,
  FilterType,
  Form,
  GridInputValue,
  IntegerInputControl,
  RealInputControl,
  ReferenceValue,
  StaticTextInputValue,
  TextAreaInputValue,
  TimeInputControl,
} from '../../../types/types';
import type { RequestDataValue } from '../../../context/RequestDataContext';
import DateTimePicker from './components/DateTimePicker/DateTimePicker';
import TimePicker from './components/TimePicker/TimePicker';
import NumericRangeFilter from './components/NumericRangeFilter/NumericRangeFilter';
import TimeRangePicker from './components/TimeRangePicker/TimeRangePicker';
import DateTimeRangePicker from './components/DateTimeRangePicker/DateTimeRangePicker';
import ColorInput from './components/ColorInput/ColorInput';
import MultiSelect from './components/MultiSelect/MultiSelect';
import PictureInput from './components/PictureInput/PictureInput';
import ReferenceInput from './components/ReferenceInput/ReferenceInput';
import Select from './components/Select/Select';
import RadioButton from './components/RadioButton/RadioButton';
import CheckboxInput from './components/CheckboxInput/CheckboxInput';
import TextAreaInput from './components/TextAreaInput/TextAreaInput';
import StaticTextInput from './components/StaticTextInput/StaticTextInput';
import StaticStringInput from './components/StaticStringInput/StaticStringInput';
import LiteralTextInput from './components/LiteralTextInput/LiteralTextInput';
import GridInputWrapper from './components/GridInputWrapper/GridInputWrapper';
import StringInput from './components/StringInput/StringInput';
import { getInputType } from './utils/getInputType';
import { NumberInput } from './components/NumberInput/NumberInput';
import { useGridInputOptions } from './hooks/useGridInputOptions';
import StaticNumberInput from './components/StaticNumberInput/StaticNumberInput';
import { isDateOnlyMatchMode } from '../../../utils/filters/isDateOnlyMatchMode';
import { parseDateTimeValue } from './utils/parseDateTimeValue';
import { parseStringValue } from './utils/parseStringValue';
import { parseNumberValue } from './utils/parseNumberValue';

export interface GridInputProps {
  name: string;
  label?: string;
  type?: FilterType;
  value?: GridInputValue;
  onChange: (value: GridInputValue, propagateHandler?: boolean) => void;
  onTouch?: (value?: unknown) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
  matchMode?: string;
  error?: FilterError;
  setError: (error: FilterError) => void;
  disabled?: boolean;
  control?: Control;
  isColumnFilter?: boolean;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  // resolvedFilter forwarded from ReferenceInput's click-time JSONata pass.
  // null sentinel = resolved to nothing (strip raw filter).
  openFullscreenReference?: (
    resolvedFilter?: Record<string, any> | null
  ) => void;
  isFormInput?: boolean;
  // Forwarded to ReferenceInput for JSONata filter resolution. `form`/`formData`
  // exist only in form scope; `requestData` is supplied by every scope (filter
  // parents read it from RequestDataContext themselves).
  form?: Form;
  formData?: any;
  requestData?: RequestDataValue;
}

const GridInput = ({
  name,
  label,
  type = 'string',
  value,
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  matchMode,
  setError,
  error = null,
  disabled = false,
  control,
  isColumnFilter = false,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
  isFormInput = false,
  form,
  formData,
  requestData,
}: GridInputProps) => {
  const inputType = getInputType(type, control);

  const { options, isLoading, handlePopoverShow } = useGridInputOptions(
    control,
    value,
    name
  );

  const isBetweenFilter =
    matchMode === 'between' || matchMode === 'datetime-between';

  const inputProps = {
    name,
    onChange,
    onTouch,
    onFocusIn,
    onFocusOut,
    disabled,
    isColumnFilter,
    error,
    setError,
  };

  if (control?.type === 'literalText') {
    return <LiteralTextInput control={control} />;
  }

  // Render boolean input
  if (control?.type === 'checkbox' || type === 'boolean') {
    const checkboxValue = typeof value === 'boolean' ? value : false;
    return (
      <CheckboxInput
        value={checkboxValue}
        label={label}
        onChange={onChange}
        onTouch={onTouch}
        onFocusIn={onFocusIn}
        onFocusOut={onFocusOut}
        control={control as CheckboxSelectControl}
        disabled={disabled}
      />
    );
  }

  // Moved staticString here, because when control.type: "staticString" and type: "date", "time" or "dateTime"
  // app should show staticString with formatted value
  if (control?.type === 'staticString') {
    const inputValue = parseStringValue(value);
    return (
      <StaticStringInput value={inputValue} control={control} type={type} />
    );
  }

  // Render date input
  if (control?.type === 'date' || type === 'date') {
    const Component = isBetweenFilter ? DateTimeRangePicker : DateTimePicker;
    const inputValue = parseDateTimeValue(value);
    return (
      <Component
        {...inputProps}
        value={inputValue}
        dateOnly
        control={control as DateTimeInputControl}
        isFormInput={isFormInput}
      />
    );
  }

  if (type === 'dateRange') {
    const inputValue = parseDateTimeValue(value);
    return (
      <DateTimeRangePicker
        {...inputProps}
        value={inputValue}
        control={control as DateTimeInputControl}
        isFormInput={isFormInput}
      />
    );
  }

  if (control?.type === 'time' || type === 'time') {
    const Component = isBetweenFilter ? TimeRangePicker : TimePicker;
    const inputValue = parseDateTimeValue(value);
    return (
      <Component
        {...inputProps}
        value={inputValue}
        control={control as TimeInputControl}
      />
    );
  }

  if (control?.type === 'dateTime' || type === 'dateTime') {
    const Component = isBetweenFilter ? DateTimeRangePicker : DateTimePicker;
    const inputValue = parseDateTimeValue(value);
    return (
      <Component
        {...inputProps}
        value={inputValue}
        dateOnly={isDateOnlyMatchMode(matchMode)}
        control={control as DateTimeInputControl}
        isFormInput={isFormInput}
      />
    );
  }

  if (control?.type === 'combobox') {
    return (
      <Select
        {...inputProps}
        value={value as ControlItemValue | null}
        options={options}
        isLoading={isLoading}
        onShow={handlePopoverShow}
        defaultValue={control.default}
      />
    );
  }

  if (control?.type === 'radioBtn') {
    return (
      <RadioButton
        {...inputProps}
        value={value as ControlItemValue | null}
        options={options}
        defaultValue={control.default}
      />
    );
  }

  if (control?.type === 'multiCheckbox') {
    const inputValue = parseStringValue(value);
    return (
      <MultiSelect
        {...inputProps}
        value={inputValue}
        options={options}
        isLoading={isLoading}
        onShow={handlePopoverShow}
      />
    );
  }

  if (control?.type === 'color') {
    const colorValue = typeof value === 'string' && value !== '' ? value : null;
    return (
      <ColorInput
        name={name}
        value={colorValue}
        onChange={onChange}
        onTouch={onTouch}
        disabled={disabled}
      />
    );
  }

  if (control?.type === 'picture') {
    const pictureValue =
      typeof value === 'string' && value !== '' ? value : null;
    return (
      <PictureInput
        name={name}
        value={pictureValue}
        onChange={onChange}
        onTouch={onTouch}
        disabled={disabled}
      />
    );
  }

  if (control?.type === 'reference') {
    const referenceValue =
      typeof value === 'boolean' || value === undefined ? null : value;
    return (
      <ReferenceInput
        {...inputProps}
        value={referenceValue as ReferenceValue}
        control={control}
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        openFullscreenReference={openFullscreenReference}
        isFormInput={isFormInput}
        form={form}
        formData={formData}
        requestData={requestData}
      />
    );
  }

  if (control?.type === 'text') {
    return (
      <TextAreaInput
        {...inputProps}
        value={value as TextAreaInputValue}
        control={control}
      />
    );
  }

  if (control?.type === 'staticText') {
    return (
      <StaticTextInput
        value={value as StaticTextInputValue}
        control={control}
      />
    );
  }

  if (control?.type === 'staticNumber') {
    const numberValue = parseNumberValue(value);
    return (
      <StaticNumberInput
        value={numberValue}
        control={control}
        isFloat={type === 'float'}
        form={form}
        formData={formData}
        requestData={requestData}
      />
    );
  }

  if (['intRange', 'numRange', 'tsRange'].includes(type)) {
    //TODO: Ask Lei what tsRange is
    return <>RANGE PICKER</>;
  }

  if (isBetweenFilter) {
    const inputValue = parseStringValue(value);
    return (
      <NumericRangeFilter
        {...inputProps}
        value={inputValue}
        type={type}
        control={control as IntegerInputControl}
      />
    );
  }

  if (
    control?.type === 'integer' ||
    control?.type === 'number' ||
    inputType === 'number'
  ) {
    const inputValue = value === '-' ? value : parseNumberValue(value);
    return (
      <NumberInput
        {...inputProps}
        value={inputValue}
        control={control as IntegerInputControl | RealInputControl}
        isFloat={type === 'float'}
        isFormInput={isFormInput}
      />
    );
  }

  const inputValue = parseStringValue(value);
  return (
    <StringInput
      {...inputProps}
      value={inputValue}
      type={inputType}
      control={control}
    />
  );
};

const withGridInputWrapper =
  (Component: React.FC<GridInputProps>) => (props: GridInputProps) => (
    <GridInputWrapper isColumnFilter={props.isColumnFilter || false}>
      <Component {...props} />
    </GridInputWrapper>
  );

export default withGridInputWrapper(GridInput);
